import type {
  CopilotProposalId,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { STATIC_AGENT_INSTRUCTION_TILES } from "@repo/app-types/entities/teammateAgent";
import type {
  TeammateAgentProposalData,
  TeammateAgentTestCaseProposalData,
} from "@repo/app-types/entities/teammateAgentProposal";
import type { To } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import {
  buildCopilotProposalLocation,
  COPILOT_PROPOSAL_QUERY_PARAM,
} from "../researchAgent/copilotDeepLinks.js";
import type { CopilotProposalCardContent } from "../researchAgent/copilotProposalCard.js";

const TEST_CASE_PROPOSAL_KINDS: ReadonlySet<TeammateAgentProposalData["kind"]> =
  new Set(["create_test_case", "update_test_case", "delete_test_case"]);

export function checkIsTestCaseProposalData(
  data: TeammateAgentProposalData,
): data is TeammateAgentTestCaseProposalData {
  return TEST_CASE_PROPOSAL_KINDS.has(data.kind);
}

/**
 * One-shot param the chat sets with `proposalId` when it opens the Testing
 * tab on a test case it just applied: the tab highlights that case and
 * reports the proposal as viewed.
 */
export const TEST_SPEC_QUERY_PARAM = "testSpecId";

/** The Testing tab, showing the test case a proposal was applied to. */
export function buildTestCaseLocation(props: {
  teammateAgentId: TeammateId;
  proposalId: CopilotProposalId;
  /** Absent for a removed test case, which has nothing left to show. */
  testSpecId?: string;
}): To & { pathname: string } {
  const searchParams = new URLSearchParams({
    [COPILOT_PROPOSAL_QUERY_PARAM]: props.proposalId,
  });
  if (props.testSpecId) {
    searchParams.set(TEST_SPEC_QUERY_PARAM, props.testSpecId);
  }
  return {
    pathname: appPaths.teammateAgentTesting.build({
      teammateAgentId: props.teammateAgentId,
    }),
    search: `?${searchParams.toString()}`,
  };
}

/**
 * What a voice agent proposal's card in the chat shows and opens. A test case
 * proposal has no editor to review in: its card applies it and opens the
 * Testing tab on the case. An instruction proposal opens the Instructions
 * tab with the tile as a diff.
 */
export function readTeammateAgentProposalCardContent(
  proposal: CopilotProposalSummary<"teammateAgent">,
): CopilotProposalCardContent {
  const teammateAgentId = proposal.resource.id;
  const { data } = proposal;
  const location = {
    teammateAgentId,
    proposalId: proposal.id,
  };
  switch (data.kind) {
    case "update_instruction_section": {
      const { label, sections } = STATIC_AGENT_INSTRUCTION_TILES[data.tile];
      const pathname = appPaths.teammateAgentInstructions.build({
        teammateAgentId,
      });
      return {
        resourceType: "voiceAgent",
        operation: "update",
        subject: "instructions",
        summary: `Rewrites the ${label} section${sections.length > 1 ? "s" : ""}.`,
        highlights: [],
        review: {
          kind: "open_editor",
          location: buildCopilotProposalLocation(pathname, proposal.id),
        },
        resourceLocation: { pathname },
      };
    }
    case "create_test_case":
      return {
        resourceType: "voiceAgent",
        operation: "create",
        subject: "test case",
        name: data.name,
        summary: data.summary,
        highlights: [],
        review: { kind: "apply_in_place", actionLabel: "Create test case" },
        resourceLocation: buildTestCaseLocation({
          ...location,
          testSpecId: data.test_spec_id,
        }),
      };
    case "update_test_case":
      return {
        resourceType: "voiceAgent",
        operation: "update",
        subject: "test case",
        name: data.proposed.name,
        summary: data.summary,
        highlights: describeTestCaseChanges(data),
        review: { kind: "apply_in_place", actionLabel: "Update test case" },
        resourceLocation: buildTestCaseLocation({
          ...location,
          testSpecId: data.test_spec_id,
        }),
      };
    case "delete_test_case":
      return {
        resourceType: "voiceAgent",
        operation: "delete",
        subject: "test case",
        name: data.baseline.name,
        summary: data.summary,
        highlights: [],
        review: { kind: "apply_in_place", actionLabel: "Remove test case" },
        resourceLocation: buildTestCaseLocation(location),
      };
  }
}

/**
 * The card's highlights: whether the update moves the assertion. The name and
 * the customer's behavior read out of the card's own title and summary, but
 * the outcome the call must reach decides what the test proves, and a test
 * case is applied from the card without an editor to show the change in.
 */
function describeTestCaseChanges(
  data: Extract<
    TeammateAgentTestCaseProposalData,
    { kind: "update_test_case" }
  >,
): readonly string[] {
  return data.proposed.expected_outcome !== data.baseline.expected_outcome
    ? ["Expected outcome"]
    : [];
}
